/**
 * Login page — POST /admin-api/v1/auth/login → branches on next_step.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn } from "lucide-react";
import { asApiError, postLogin } from "@/lib/api";
import { useAuth } from "@/lib/use-auth";
import { safeRedirectPath } from "@/lib/redirect";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const NEXT_STEP_PATH = {
  totp_enroll: "/totp/enroll",
  totp_verify: "/totp/verify",
  change_password: "/change-password",
} as const;

const ERROR_COPY: Record<string, string> = {
  invalid_credentials: "Wrong email or password.",
  account_locked:
    "Too many failed attempts. This account is temporarily locked — try again in 15 minutes.",
};

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordChanged = params.get("changed") === "1";
  const passwordReset = params.get("reset") === "1";

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await postLogin({
        email: email.trim().toLowerCase(),
        password,
      });
      const dest = NEXT_STEP_PATH[r.next_step];
      await refresh();
      // Not a full session until the code is in: going straight to `next`
      // would bounce back here with an empty form. TOTPVerify honours it.
      const next = safeRedirectPath(params.get("next"));
      if (next && r.next_step === "totp_verify") {
        navigate(`${dest}?next=${encodeURIComponent(next)}`, { replace: true });
      } else {
        navigate(dest, { replace: true });
      }
    } catch (err) {
      const body = asApiError(err);
      if (body) {
        setError(ERROR_COPY[body.error] ?? `Sign-in failed (${body.error}).`);
      } else {
        setError("Sign-in failed. Check your network and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Welcome back"
      description="Sign in to your Senti admin account."
      footer="Protected by session cookies + TOTP."
    >
      {passwordChanged ? (
        <div className="mb-4 rounded-md border border-success/40 bg-success/10 p-3 text-sm text-success">
          Password changed. Sign in with your new credentials.
        </div>
      ) : null}
      {passwordReset ? (
        <div className="mb-4 rounded-md border border-success/40 bg-success/10 p-3 text-sm text-success">
          Password updated. Sign in with your new password and authenticator code.
        </div>
      ) : null}
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            placeholder="you@example.com"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            placeholder="••••••••••••"
          />
        </div>
        {error ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {error}
          </div>
        ) : null}
        <Button type="submit" className="w-full" disabled={submitting} size="lg">
          <LogIn />
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}

export default LoginPage;
